import React, { useEffect, useState } from "react";
import { Header } from "@/components/layouts/Header";
import { SidePanel } from "@/components/organisms/SidePanel";
import { useNavigationStore } from "@/stores/navigation";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const aiLoadingMessages = [
  "🧠 Training neural networks...",
  "🤖 Optimizing AI models...",
  "✨ Generating intelligent insights...",
  "🔮 Predicting optimal outcomes...",
  "🎯 Analyzing data patterns...",
  "🚀 Launching AI agents...",
  "💡 Crafting smart solutions...",
  "🌟 Enhancing with machine learning...",
  "📊 Processing with deep learning...",
  "🎨 Creating AI-powered magic...",
];

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
}) => {
  const { isNavigating } = useNavigationStore();
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!isNavigating) {
      setMessageIndex(0);
      setIsVisible(true);
      document.body.style.overflow = 'unset';
      return;
    }

    // Prevent body scroll when loader is active
    document.body.style.overflow = 'hidden';

    const interval = setInterval(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % aiLoadingMessages.length);
        setIsVisible(true);
      }, 300);
    }, 3000);

    return () => {
      clearInterval(interval);
      document.body.style.overflow = 'unset';
    };
  }, [isNavigating]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/20">
      <Header />
      <div className="flex h-[calc(100vh-73px)] relative">
        <SidePanel className="hidden lg:flex" />
        <div className="flex-1 overflow-auto min-w-0 relative">
          {isNavigating && (
            <div className="fixed inset-0 bg-gradient-to-br from-background/60 via-background/50 to-background/60 backdrop-blur-xl z-[9999] flex items-center justify-center overflow-hidden">
              {/* Animated background particles */}
              <div className="absolute inset-0">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full mix-blend-soft-light dark:mix-blend-screen animate-float"
                    style={{
                      width: `${Math.random() * 400 + 100}px`,
                      height: `${Math.random() * 400 + 100}px`,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      background: `radial-gradient(circle, ${
                        i % 2 === 0 ? 'rgba(139, 92, 246, 0.15)' : 'rgba(168, 85, 247, 0.15)'
                      } 0%, transparent 70%)`,
                      animationDelay: `${i * 0.5}s`,
                      animationDuration: `${10 + i * 2}s`,
                    }}
                  />
                ))}
              </div>
              
              <div className="relative">
                {/* Multi-layer animated glow */}
                <div className="absolute inset-0 -m-24">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-accent/30 to-primary/30 dark:from-purple-500/30 dark:via-purple-400/30 dark:to-purple-500/30 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute inset-4 bg-gradient-to-r from-accent/20 via-primary/20 to-accent/20 dark:from-purple-400/20 dark:via-purple-500/20 dark:to-purple-400/20 rounded-full blur-2xl animate-pulse animation-delay-200" />
                  <div className="absolute inset-8 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 dark:from-purple-500/10 dark:via-purple-400/10 dark:to-purple-500/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '400ms' }} />
                </div>

                {/* Modern Liquid Loader */}
                <div className="relative">
                  {/* Main morphing blob container */}
                  <div className="relative w-48 h-48">
                    {/* Background soft glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-accent/30 to-primary/30 dark:from-purple-500/40 dark:via-purple-400/40 dark:to-purple-500/40 blur-3xl animate-pulse" />
                    
                    {/* Liquid blobs */}
                    <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{ filter: 'url(#goo)' }}>
                      <defs>
                        <filter id="goo">
                          <feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="10" />
                          <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8" result="goo" />
                          <feComposite in="SourceGraphic" in2="goo" operator="atop" />
                        </filter>
                      </defs>
                      
                      {/* Morphing blobs */}
                      <circle 
                        className="fill-primary dark:fill-primary"
                        cx="50%" 
                        cy="50%" 
                        r="25"
                        style={{
                          animation: 'morphBlob1 8s ease-in-out infinite',
                          transformOrigin: 'center',
                          opacity: 0.9,
                        }}
                      />
                      <circle 
                        className="fill-accent dark:fill-purple-400"
                        cx="50%" 
                        cy="50%" 
                        r="25"
                        style={{
                          animation: 'morphBlob2 8s ease-in-out infinite',
                          transformOrigin: 'center',
                          opacity: 0.8,
                        }}
                      />
                      <circle 
                        className="fill-primary/80 dark:fill-purple-500/80"
                        cx="50%" 
                        cy="50%" 
                        r="20"
                        style={{
                          animation: 'morphBlob3 8s ease-in-out infinite',
                          transformOrigin: 'center',
                          opacity: 0.7,
                        }}
                      />
                    </svg>
                    
                    {/* Center dot */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 bg-white dark:bg-white/90 rounded-full shadow-2xl shadow-white/50 dark:shadow-purple-400/50" />
                    </div>
                  </div>

                  {/* Loading text with modern dots */}
                  <div className="mt-12 text-center space-y-4">
                    <div className="flex items-center justify-center gap-1">
                      {[...Array(3)].map((_, i) => (
                        <div
                          key={i}
                          className="w-3 h-3 rounded-full bg-gradient-to-br from-primary to-accent"
                          style={{
                            animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite`,
                          }}
                        />
                      ))}
                    </div>
                    <div className="relative h-8 flex items-center justify-center">
                      <p 
                        className={`text-sm text-muted-foreground/80 font-medium transition-all duration-300 absolute ${
                          isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-2'
                        }`}
                      >
                        {aiLoadingMessages[messageIndex]}
                      </p>
                    </div>
                  </div>
                </div>
                
                <style>{`
                  @keyframes morphBlob1 {
                    0%, 100% {
                      transform: translate(0px, 0px) scale(1);
                    }
                    33% {
                      transform: translate(30px, -30px) scale(1.1);
                    }
                    66% {
                      transform: translate(-20px, 20px) scale(0.9);
                    }
                  }
                  
                  @keyframes morphBlob2 {
                    0%, 100% {
                      transform: translate(0px, 0px) scale(1);
                    }
                    33% {
                      transform: translate(-30px, 20px) scale(1.1);
                    }
                    66% {
                      transform: translate(30px, -10px) scale(0.85);
                    }
                  }
                  
                  @keyframes morphBlob3 {
                    0%, 100% {
                      transform: translate(0px, 0px) scale(1);
                    }
                    33% {
                      transform: translate(20px, 30px) scale(0.8);
                    }
                    66% {
                      transform: translate(-30px, -20px) scale(1.2);
                    }
                  }
                `}</style>
              </div>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
